/**
 * Responsive Editor Canvas
 *
 * CSS-first responsive canvas implementation that mirrors ViewerCanvas architecture.
 * Enables browser inspect-mode viewport switching with real-time responsive preview.
 *
 * Key Features:
 * - Percentage-based positioning instead of absolute pixels
 * - Seamless viewport transitions without position recalculation
 * - 100% visual parity with viewer mode
 * - Spatial indexing integration for performance
 * - Multi-page support with viewport state persistence
 *
 * Architecture Decision: Replaces absolute positioning system with responsive
 * CSS Grid/Flexbox hybrid for maintainable, scalable responsive design.
 */

import React, { useCallback, useMemo, useRef, useEffect, useState } from "react";
import { Component, EditorPanel, Position, Size } from "@/types";
import type { ColorScheme } from "@/types/color-scheme";
import {
  useResponsiveStore,
  ResponsiveBreakpoint,
} from "@/stores/editor/responsive-store";
import { useDebouncedCallback } from "@/hooks/useDebounce";
import { useEditorStore } from "@/stores/editor/editor-store";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { ComponentWrapper } from "@/components/shared/rendering/ComponentWrapper";
import { PerformanceHints } from "@/components/shared/rendering/SharedComponentRenderer";
import {
  InteractionContextProvider,
  InteractionHandlers,
  UserPermissions,
} from "@/components/shared/rendering/InteractionContext";
import { ComponentBounds, PERFORMANCE_CONFIG } from "@/types/canvas-legacy-types";
import { calculateResponsiveCanvasDimensions } from "@/utils/canvas-dimensions";
import { viewportTransitionManager } from "@/services/viewport/ViewportTransitionManager";
import { SketchOverlay } from "./SketchOverlay";
import {
  useContainerDimensions,
} from "@/components/viewer/ViewerLayoutManager";
import {
  calculateResponsivePosition,
  calculateResponsiveSize,
  applyResponsiveConstraints,
  ViewportBounds,
} from "@/utils/responsive-position-calculator";
import { calculateViewportScale } from "@/utils/viewport-scale";
import { useMarqueeSelection } from "@/hooks/editor/useMarqueeSelection";
import {
  getRenderableComponentStyle,
  type StyleBreakpoint,
} from "@/utils/component-style-normalization";
import { normalizeLegacyCanvasBackgroundColor } from "@/utils/theme-aware-legacy-colors";
import { MarqueeOverlay } from "./MarqueeOverlay";
import { SelectionCounter } from "./SelectionCounter";
import { ChevronRight, FileText, Workflow } from "lucide-react";
import { PageSelector } from "./PageSelector";
import { ModalPreviewButton } from "./ModalPreviewButton";
import { TableModalPreviewProvider } from "@/components/tables/table-modal-preview-context";
import { WorkflowSelector } from "./WorkflowSelector";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { ComponentType } from "@/types";
import { useOptionalTheme } from "@/design-system/themes/theme-context";
import { logger } from "@/utils/logger";

type NormalizedBounds = {
  x: number;
  y: number;
  width: number;
  height: number;
};

const CANVAS_PADDING = 24;
const BOUNDS_EPSILON = 0.5;
const toFiniteNumber = (value: unknown, fallback: number): number => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  return fallback;
};

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === "object" && value !== null && !Array.isArray(value)
);

const isSizeValue = (value: unknown): value is Size[keyof Size] => (
  typeof value === "number" || typeof value === "string"
);

const getInteractionSize = (data: unknown): Size | undefined => {
  if (!isRecord(data) || !isRecord(data.size)) {
    return undefined;
  }

  const { width, height } = data.size;
  return isSizeValue(width) && isSizeValue(height)
    ? { width, height }
    : undefined;
};

const getInteractionPosition = (data: unknown): Position | undefined => {
  if (!isRecord(data) || !isRecord(data.position)) {
    return undefined;
  }

  const { x, y, z } = data.position;
  const normalizedX = toFiniteNumber(x, Number.NaN);
  const normalizedY = toFiniteNumber(y, Number.NaN);

  if (!Number.isFinite(normalizedX) || !Number.isFinite(normalizedY)) {
    return undefined;
  }

  return {
    x: normalizedX,
    y: normalizedY,
    z: toFiniteNumber(z, 1),
  };
};

const getInteractionMultiSelect = (data: unknown): boolean => (
  isRecord(data) && data.multiSelect === true
);

const toStyleBreakpoint = (viewportId: string): StyleBreakpoint => {
  if (viewportId === "mobile") return "mobile";
  if (viewportId === "tablet") return "tablet";
  return "desktop";
};

const areViewportBoundsEqual = (
  a: ViewportBounds | null,
  b: ViewportBounds | null,
): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;

  return (
    Math.abs(a.x - b.x) < BOUNDS_EPSILON &&
    Math.abs(a.y - b.y) < BOUNDS_EPSILON &&
    Math.abs(a.width - b.width) < BOUNDS_EPSILON &&
    Math.abs(a.height - b.height) < BOUNDS_EPSILON
  );
};

/**
 * Spatial index interface for type safety (stub - spatial indexing removed)
 */
interface SpatialIndex {
  cullByViewport: (bounds: { x: number; y: number; width: number; height: number }, buffer?: number) => {
    visible: ComponentBounds[];
    culled: ComponentBounds[];
  };
}

interface ResponsiveEditorCanvasProps {
  viewport: ResponsiveBreakpoint;
  isActive: boolean;
  isVisible: boolean;
  scale: number; // User zoom only, not viewport scaling
  components: Component[];
  selectedComponents: string[];
  hoveredComponent?: string;
  onComponentSelect: (id: string, ctrlKey?: boolean) => void;
  onCanvasClick: (e: React.MouseEvent) => void;
  onComponentUpdate: (id: string, updates: Partial<Component>) => void;
  className?: string;
  spatialIndex?: SpatialIndex | null; // Spatial indexing removed - kept for backward compatibility
  activeColorScheme?: ColorScheme;
}

/**
 * Responsive Editor Canvas
 *
 * Main canvas component with CSS-first responsive positioning.
 */
export const ResponsiveEditorCanvas: React.FC<ResponsiveEditorCanvasProps> = ({
  viewport,
  isActive,
  isVisible,
  scale: _scale,
  components,
  selectedComponents,
  hoveredComponent: _hoveredComponent,
  onComponentSelect,
  onCanvasClick,
  onComponentUpdate,
  className,
  spatialIndex,
  activeColorScheme,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const [canvasElement, setCanvasElement] = useState<HTMLDivElement | null>(null);
  const [scrollContainerElement, setScrollContainerElement] = useState<HTMLDivElement | null>(null);
  const [scrollContainerHeight, setScrollContainerHeight] = useState(0);
  const [visibleBounds, setVisibleBounds] = useState<ViewportBounds | null>(null);

  const availableViewports = useResponsiveStore(
    (state) => state.availableViewports,
  );
  const isTransitioning = useResponsiveStore((state) => state.isTransitioning);
  const transitionProgress = useResponsiveStore(
    (state) => state.transitionProgress,
  );
  const updateTransitionProgress = useResponsiveStore(
    (state) => state.updateTransitionProgress,
  );
  const cullingEnabled = useResponsiveStore((state) => state.cullingEnabled);
  const visibilityThreshold = useResponsiveStore(
    (state) => state.visibilityThreshold,
  );

  const getResponsiveProperty = useResponsiveStore(
    (state) => state.getResponsiveProperty,
  );

  // Week 2: Dual Scale System - Get viewport scale and user zoom from store
  const setViewportScale = useResponsiveStore((state) => state.setViewportScale);
  const getViewportScale = useResponsiveStore((state) => state.getViewportScale);
  const userZoom = useResponsiveStore((state) => state.userZoom);

  const { canvasSettings, moveComponent, updateComponent, project, currentPage } = useEditorStore();
  const setActiveColorScheme = useEditorStore((state) => state.setActiveColorScheme);
  const theme = useOptionalTheme();
  const renderColorScheme = activeColorScheme ?? theme?.resolvedTheme;
  const enterWorkflowMode = useEditorStore((state) => state.enterWorkflowMode);
  const editorMode = useEditorStore((state) => state.editorMode);

  useEffect(() => {
    setActiveColorScheme(renderColorScheme);
  }, [renderColorScheme, setActiveColorScheme]);

  // Check if selected component is a form with a connected workflow
  const selectedFormWorkflowId = useMemo(() => {
    const ids = selectedComponents ?? [];
    const comps = components ?? [];
    if (ids.length !== 1) return null;
    if (comps.length === 0) return null;
    const selectedComponent = comps.find((c) => c.id === ids[0]);
    const workflowId = selectedComponent?.properties?.workflowId;
    if (
      selectedComponent?.type === ComponentType.FORM_CONTAINER &&
      typeof workflowId === "string"
    ) {
      return workflowId;
    }
    return null;
  }, [selectedComponents, components]);

  const containerDimensions = useContainerDimensions(scrollContainerRef);
  const responsiveContainerDimensions = useMemo(() => {
    const fallbackWidth = viewport.width;
    const fallbackHeight = viewport.height;

    const width = Number.isFinite(containerDimensions.width) && containerDimensions.width > 0
      ? Math.min(containerDimensions.width, viewport.width)
      : fallbackWidth;
    const height = Number.isFinite(containerDimensions.height) && containerDimensions.height > 0
      ? Math.max(containerDimensions.height, viewport.height)
      : fallbackHeight;

    return { width, height };
  }, [containerDimensions.width, containerDimensions.height, viewport.width, viewport.height]);

  // Get base viewport for responsive calculations
  const baseViewport = useMemo(
    () => availableViewports.find((vp) => vp.id === "desktop") || viewport,
    [availableViewports, viewport]
  );

  // Calculate responsive canvas dimensions
  const canvasDimensions = useMemo(
    () => {
      return calculateResponsiveCanvasDimensions(
        components,
        viewport,
        baseViewport,
        100,
        {
          getResponsiveProperty,
        }
      );
    },
    [components, viewport, baseViewport, getResponsiveProperty]
  );

  // Week 2: Calculate and apply viewport scale automatically
  const calculateAndStoreViewportScale = useCallback(() => {
    // Wait for container dimensions to be available
    if (!containerDimensions.width || !containerDimensions.height) {
      return;
    }

    // Performance marker - start
    performance.mark('viewport-scale-calc-start');

    // Calculate viewport scale to fit viewport in available canvas space
    const calculatedScale = calculateViewportScale(
      containerDimensions.width,
      containerDimensions.height,
      viewport.width,
      viewport.height,
      0.9 // 10% padding
    );

    // Store viewport scale in responsive store
    setViewportScale(viewport.id, calculatedScale);

    // Performance marker - end
    performance.mark('viewport-scale-calc-end');
    performance.measure(
      'viewport-scale-calculation',
      'viewport-scale-calc-start',
      'viewport-scale-calc-end'
    );
  }, [
    containerDimensions.width,
    containerDimensions.height,
    viewport.width,
    viewport.height,
    viewport.id,
    setViewportScale,
  ]);

  // Week 2 Day 6.2: Debounced version to reduce recalculation frequency
  const debouncedCalculateViewportScale = useDebouncedCallback(
    calculateAndStoreViewportScale,
    300 // 300ms delay - balance between responsiveness and performance
  );

  // Calculate viewport scale on mount and when dependencies change
  // Week 2 Day 6.2: Using debounced version to reduce recalculation frequency (300ms delay)
  useEffect(() => {
    debouncedCalculateViewportScale();
  }, [debouncedCalculateViewportScale]);

  // Get the stored viewport scale for this viewport
  const viewportScale = getViewportScale(viewport.id);

  // Week 2: Calculate effective scale (viewport scale × user zoom)
  // Note: The 'scale' prop represents user zoom only (as clarified in user answers)
  // But we use userZoom from store for consistency
  const effectiveScale = useMemo(() => {
    return viewportScale * userZoom;
  }, [viewportScale, userZoom]);

  // Week 3: Marquee selection for multi-component selection
  const { marqueeRectangle, isActive: isMarqueeActive } = useMarqueeSelection({
    enabled: isActive, // Only enable marquee in active viewport
    canvasElement, // Pass element state instead of ref
    scrollContainerElement, // Allow marquee to start from scroll container (grey area)
    scale: effectiveScale,
    minimumDragDistance: 5,
  });

  const enableVirtualization = useMemo(() => {
    if (!cullingEnabled) {
      return false;
    }

    if (components.length <= PERFORMANCE_CONFIG.SPATIAL_INDEX_THRESHOLD) {
      return false;
    }

    return true;
  }, [cullingEnabled, components.length]);

  const resolveVisibleBounds = useCallback(
    (element: HTMLDivElement): ViewportBounds => {
      const horizontalPadding = CANVAS_PADDING * 2;
      const verticalPadding = CANVAS_PADDING * 2;

      const adjustedWidth = Math.max(0, element.clientWidth - horizontalPadding);
      const adjustedHeight = Math.max(0, element.clientHeight - verticalPadding);

      const normalizedX = Math.max(0, element.scrollLeft - CANVAS_PADDING);
      const normalizedY = Math.max(0, element.scrollTop - CANVAS_PADDING);

      // Week 2: Use effectiveScale for viewport culling calculations
      return {
        x: normalizedX / Math.max(effectiveScale, 0.0001),
        y: normalizedY / Math.max(effectiveScale, 0.0001),
        width: (adjustedWidth > 0 ? adjustedWidth : element.clientWidth) /
          Math.max(effectiveScale, 0.0001),
        height: (adjustedHeight > 0 ? adjustedHeight : element.clientHeight) /
          Math.max(effectiveScale, 0.0001),
      };
    },
    [effectiveScale],
  );

  const updateVisibleBoundsState = useCallback((next: ViewportBounds | null) => {
    setVisibleBounds((prev) => {
      if (areViewportBoundsEqual(prev, next)) {
        return prev;
      }
      return next;
    });
  }, []);

  const computeComponentBounds = useCallback(
    (component: Component): NormalizedBounds | null => {
      const basePosition: Position = {
        x: toFiniteNumber(
          getResponsiveProperty(component.id, "position.x", viewport.id),
          component.position?.x ?? 0,
        ),
        y: toFiniteNumber(
          getResponsiveProperty(component.id, "position.y", viewport.id),
          component.position?.y ?? 0,
        ),
        z: toFiniteNumber(
          getResponsiveProperty(component.id, "position.z", viewport.id),
          component.position?.z ?? 1,
        ),
      };

      const baseSize = {
        width: toFiniteNumber(
          getResponsiveProperty(component.id, "size.width", viewport.id),
          toFiniteNumber(component.size?.width, 0),
        ),
        height: toFiniteNumber(
          getResponsiveProperty(component.id, "size.height", viewport.id),
          toFiniteNumber(component.size?.height, 0),
        ),
      };

      if (baseSize.width <= 0 || baseSize.height <= 0) {
        return null;
      }

      const responsivePosition = calculateResponsivePosition({
        basePosition,
        baseViewport,
        targetViewport: viewport,
        containerDimensions: {
          width: viewport.width,
          height: viewport.height,
        },
      });

      const responsiveSize = calculateResponsiveSize(baseSize, baseViewport, viewport);

      const { position, size } = applyResponsiveConstraints(
        responsivePosition.position,
        { width: responsiveSize.width, height: responsiveSize.height },
        viewport,
        {
          minWidth: 1,
          minHeight: 1,
        },
      );

      return {
        x: position.x,
        y: position.y,
        width: toFiniteNumber(size.width, 0),
        height: toFiniteNumber(size.height, 0),
      };
    },
    [
      getResponsiveProperty,
      viewport,
      baseViewport,
    ],
  );

  const expandedViewportBounds = useMemo(() => {
    if (!visibleBounds) {
      return null;
    }

    const horizontalBuffer = visibleBounds.width * visibilityThreshold;
    const verticalBuffer = visibleBounds.height * visibilityThreshold;
    const pixelBuffer = PERFORMANCE_CONFIG.VIEWPORT_CULL_BUFFER;

    const nextBounds: ViewportBounds = {
      x: Math.max(0, visibleBounds.x - pixelBuffer - horizontalBuffer),
      y: Math.max(0, visibleBounds.y - pixelBuffer - verticalBuffer),
      width: visibleBounds.width + (pixelBuffer + horizontalBuffer) * 2,
      height: visibleBounds.height + (pixelBuffer + verticalBuffer) * 2,
    };

    return nextBounds;
  }, [visibleBounds, visibilityThreshold]);

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (!enableVirtualization) {
        return;
      }

      const element = event.currentTarget;
      updateVisibleBoundsState(resolveVisibleBounds(element));
    },
    [enableVirtualization, resolveVisibleBounds, updateVisibleBoundsState],
  );

  useEffect(() => {
    if (!enableVirtualization) {
      updateVisibleBoundsState(null);
      return;
    }

    if (!scrollContainerRef.current) {
      return;
    }

    updateVisibleBoundsState(resolveVisibleBounds(scrollContainerRef.current));
  }, [
    enableVirtualization,
    resolveVisibleBounds,
    containerDimensions.width,
    containerDimensions.height,
    updateVisibleBoundsState,
  ]);

  // Filter visible components with spatial indexing
  const visibleComponents = useMemo(() => {
    const baseComponents = components.filter((component) => !component.hidden && !component.isDeleted);

    if (!enableVirtualization || !expandedViewportBounds) {
      return baseComponents;
    }

    if (spatialIndex && baseComponents.length > PERFORMANCE_CONFIG.SPATIAL_INDEX_THRESHOLD) {
      try {
        const cullingResult = spatialIndex.cullByViewport(
          expandedViewportBounds,
          PERFORMANCE_CONFIG.VIEWPORT_CULL_BUFFER,
        );

        const visibleIds = new Set<string>();
        cullingResult.visible.forEach((bounds: ComponentBounds) => visibleIds.add(bounds.id));

        // Always include root-level components to maintain layout structure
        baseComponents.forEach((component) => {
          if (!component.parentId) {
            visibleIds.add(component.id);
          }
        });

        if (visibleIds.size === 0) {
          return baseComponents;
        }

        return baseComponents.filter((component) => visibleIds.has(component.id));
      } catch (error) {
        logger.error("Spatial indexing culling error:", error);
        return baseComponents;
      }
    }

    return baseComponents.filter((component) => {
      if (!component.parentId) {
        return true;
      }

      const bounds = computeComponentBounds(component);
      if (!bounds) {
        return false;
      }

      const componentRight = bounds.x + bounds.width;
      const componentBottom = bounds.y + bounds.height;
      const viewportRight = expandedViewportBounds.x + expandedViewportBounds.width;
      const viewportBottom = expandedViewportBounds.y + expandedViewportBounds.height;

      return !(
        componentRight < expandedViewportBounds.x ||
        bounds.x > viewportRight ||
        componentBottom < expandedViewportBounds.y ||
        bounds.y > viewportBottom
      );
    });
  }, [
    components,
    enableVirtualization,
    expandedViewportBounds,
    spatialIndex,
    computeComponentBounds,
  ]);

  const performanceHints = useMemo<PerformanceHints>(() => {
    const fallbackBounds: ViewportBounds = expandedViewportBounds || {
      x: 0,
      y: 0,
      width: canvasDimensions.width,
      height: canvasDimensions.height,
    };

    return {
      enableVirtualization,
      spatialIndex: spatialIndex ?? undefined,
      viewportBounds: fallbackBounds,
    };
  }, [
    expandedViewportBounds,
    enableVirtualization,
    spatialIndex,
    canvasDimensions.width,
    canvasDimensions.height,
  ]);

  // Subscribe to transition progress
  useEffect(() => {
    const unsubscribe = viewportTransitionManager.onTransitionProgress((state) => {
      updateTransitionProgress(state.progress);
    });

    return unsubscribe;
  }, [updateTransitionProgress]);

  // Handle component position changes
  const handlePositionChange = useCallback(
    (componentId: string, position: Position) => {
      moveComponent(componentId, position);
      onComponentUpdate(componentId, { position });
    },
    [moveComponent, onComponentUpdate]
  );

  // Handle component size changes
  const handleSizeChange = useCallback(
    (componentId: string, size: Size) => {
      updateComponent(componentId, { size });
      onComponentUpdate(componentId, { size });
    },
    [updateComponent, onComponentUpdate]
  );

  // Handle canvas click
  const handleCanvasClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onCanvasClick(e);
      }
    },
    [onCanvasClick]
  );

  // Track scroll container height so canvas can fill the visible viewport area
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setScrollContainerHeight(entry.contentRect.height);
      }
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, [scrollContainerElement]);

  // Calculate dynamic canvas height based on content
  // For relative positioning, components stack vertically, so total height is sum of all heights
  // NOTE: We need to depend on the actual components array, not visibleComponents
  // Week 2: Robust height parsing to prevent NaN propagation
  const parseHeightValue = useCallback((height: unknown): number => {
    if (typeof height === 'number' && Number.isFinite(height)) {
      return height;
    }

    if (typeof height === 'string') {
      // Handle pixel values: "300px" → 300
      if (height.endsWith('px')) {
        const num = parseFloat(height);
        return Number.isFinite(num) ? num : 0;
      }

      // Skip flex values that don't contribute to content height
      // These are handled by browser's flex layout calculation
      if (height === 'auto' || height === 'inherit' || height.includes('%')) {
        return 0;
      }

      // Try parsing as plain number string
      const num = parseFloat(height);
      return Number.isFinite(num) ? num : 0;
    }

    return 0; // Fallback for undefined/null
  }, []);

  const calculatedContentHeight = useMemo(() => {
    const topLevelComponents = components.filter((c) => !c.parentId && !c.hidden && !c.isDeleted);
    const activeStyleBreakpoint = toStyleBreakpoint(viewport.id);

    if (topLevelComponents.length === 0) {
      return canvasDimensions.height; // Use default height when no components
    }

    // Sum up all top-level component heights (they stack vertically with relative positioning)
    const totalHeight = topLevelComponents.reduce((sum, component) => {
      // Prefer style.height (used by AI chat) over size.height
      // This ensures updates from AI chat are reflected immediately
      const normalizedStyle = getRenderableComponentStyle(
        component,
        activeStyleBreakpoint,
        renderColorScheme,
      );
      const styleHeight = parseHeightValue(normalizedStyle.height);
      const sizeHeight = parseHeightValue(component.size?.height);

      const componentHeight = styleHeight || sizeHeight;

      // Validate before adding to sum to prevent NaN propagation
      if (!Number.isFinite(componentHeight)) {
        logger.warn("Invalid component height while calculating content height", {
          componentId: component.id,
          styleHeight: normalizedStyle.height,
          sizeHeight: component.size?.height,
        });
        return sum; // Don't add NaN to sum
      }

      return sum + componentHeight;
    }, 0);

    // Add some padding at the bottom (16px)
    const finalHeight = totalHeight + 16;

    return finalHeight;
  }, [components, canvasDimensions.height, parseHeightValue, viewport.id, renderColorScheme]);

  // Determine final canvas height: max of minimum height, calculated content height,
  // and available viewport height (so page background fills the visible area).
  const dynamicCanvasHeight = useMemo(() => {
    // Account for scroll container's visible height so the canvas background
    // fills the viewport. Divide by effectiveScale since the canvas is rendered
    // via CSS transform: scale(). Subtract inner wrapper padding (8px × 2).
    const viewportFillHeight = scrollContainerHeight > 0
      ? Math.ceil((scrollContainerHeight - 16) / effectiveScale)
      : 0;
    const result = Math.max(canvasDimensions.height, calculatedContentHeight, viewportFillHeight);

    // Final safety check to prevent NaN propagation
    if (!Number.isFinite(result)) {
      logger.error("Invalid dynamic canvas height detected", {
        canvasDimensionsHeight: canvasDimensions.height,
        calculatedContentHeight,
        viewportFillHeight,
        fallbackTo: canvasDimensions.height,
      });
      return canvasDimensions.height; // Fallback to viewport height
    }

    return result;
  }, [canvasDimensions.height, calculatedContentHeight, scrollContainerHeight, effectiveScale]);

  const canvasBackgroundStyle = useMemo<React.CSSProperties>(() => {
    const background = canvasSettings.background;

    if (background.type === "color") {
      return {
        backgroundColor: normalizeLegacyCanvasBackgroundColor(background.color),
      };
    }

    if (background.type === "gradient" && background.gradient) {
      return {
        background: `linear-gradient(${
          background.gradient.direction
        }, ${background.gradient.stops
          .map((stop) => `${normalizeLegacyCanvasBackgroundColor(stop.color)} ${stop.position}%`)
          .join(", ")})`,
      };
    }

    return {};
  }, [canvasSettings.background]);

  // User permissions for editor mode
  const userPermissions: UserPermissions = useMemo(() => ({
    canEdit: true,
    canView: true,
    canInteract: false, // No functional interactions in edit mode
    isOwner: true,
  }), []);

  // Interaction handlers for editor mode
  const interactionHandlers: InteractionHandlers = useMemo(() => ({
    onSelect: (componentId: string) => {
      onComponentSelect(componentId);
    },

    onResize: (componentId: string, bounds) => {
      handleSizeChange(componentId, { width: bounds.width, height: bounds.height });
    },

    onDelete: (componentId: string) => {
      // Deletion handled through editor store
      const { deleteComponent } = useEditorStore.getState();
      deleteComponent(componentId);
    },

    onEditProperties: (componentId: string) => {
      const { selectComponent, setActivePanel } = useEditorStore.getState();
      selectComponent(componentId);
      setActivePanel(EditorPanel.PROPERTIES); // Switch to properties panel
    },

    onToggleVisibility: (targetIds: string[], showOnlyThisId?: string) => {
      const { components, updateComponent } = useEditorStore.getState();
      const getComp = (id: string) => components.find(c => c.id === id);

      if (showOnlyThisId) {
        // Tab/accordion mode: show one, hide others
        targetIds.forEach(id => {
          const comp = getComp(id);
          if (comp) {
            updateComponent(id, { hidden: id !== showOnlyThisId });
          }
        });
      } else {
        // Simple toggle: flip hidden state for all targets
        targetIds.forEach(id => {
          const comp = getComp(id);
          if (comp) {
            updateComponent(id, { hidden: !comp.hidden });
          }
        });
      }
    },
  }), [onComponentSelect, handleSizeChange]);

  if (!isVisible) return null;

  return (
    <InteractionContextProvider
      // Week 1 Refactor: Use "viewing" mode for flex layout positioning instead of absolute positioning
      // This achieves 100% visual parity with viewer mode (AI-first editing workflow)
      mode="viewing"
      userPermissions={userPermissions}
      handlers={interactionHandlers}
    >
      <TableModalPreviewProvider>
        <Card
          className={cn(
            "flex flex-col min-h-[400px] overflow-hidden",
            "rounded-lg border-0 bg-background shadow-none",
            isTransitioning && "shadow-lg shadow-blue-500/20",
            className
          )}
        >
        {/* Canvas Header */}
        <div
          className={cn(
            "flex items-center justify-between px-4 py-2.5 border-b",
            "transition-all duration-200",
            isTransitioning && "border-blue-500/60 bg-blue-500/10"
          )}
          style={{
            backgroundColor: isActive && !isTransitioning ? `hsl(var(--primary) / 0.03)` : 'transparent',
            borderColor: isActive && !isTransitioning ? `hsl(var(--primary) / 0.19)` : `hsl(var(--border))`,
          }}
        >
          <div className="flex items-center gap-2">
            {/* Primary accent bar */}
            <div className="w-1 h-5 rounded-full bg-primary" />
            {/* Breadcrumb navigation: Project > Page */}
            <nav
              className="flex items-center gap-1"
              aria-label="Breadcrumb"
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              {project && (
                <span className="text-sm font-medium tracking-tight text-muted-foreground">
                  {project.name || `Project: ${project.id?.substring(0, 8)}...`}
                </span>
              )}
              {currentPage && (
                <>
                  <ChevronRight
                    className="w-3.5 h-3.5 flex-shrink-0 text-muted-foreground/50"
                    aria-hidden="true"
                  />
                  <span className="flex items-center gap-1.5 text-sm font-medium tracking-tight text-primary">

                    <FileText className="w-3.5 h-3.5" aria-hidden="true" />
                    {currentPage.name || 'Untitled Page'}
                  </span>
                </>
              )}
            </nav>
            {isTransitioning && (
              <span className="text-sm text-blue-500 animate-pulse ml-2">Transitioning...</span>
            )}
            {/* Form Workflow Quick Access Button */}
            {selectedFormWorkflowId && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-6 gap-1 ml-3 text-xs border-primary/30 bg-primary/5 hover:bg-primary/10"
                      onClick={() => enterWorkflowMode(selectedFormWorkflowId)}
                    >
                      <Workflow className="h-3 w-3" />
                      <span>Workflow</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    Open the workflow connected to this form
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {/* Modal Preview Indicator */}
            <ModalPreviewButton />
          </div>

          {/* Right side - Page/Workflow Selector */}
          {editorMode === 'workflow' ? <WorkflowSelector /> : <PageSelector />}
        </div>

        {/* Canvas Area with CSS-first responsive design */}
      <div
        ref={(el) => {
          if (el) {
            scrollContainerRef.current = el;
            setScrollContainerElement(el);
          }
        }}
        onScroll={handleScroll}
        className={cn(
          "flex-1 relative overflow-auto",
          "transition-all duration-300 ease-out",
          "bg-muted/50",
          visibleComponents.length === 0 &&
            "bg-gradient-to-br from-muted/30 to-muted/50"
        )}
        style={{
          width: "100%",
          height: "100%",
          position: "relative",
        }}
      >
        {/* Inner wrapper provides breathing room and centers the responsive canvas */}
        <div
          className="relative flex justify-center"
          style={{
            // Week 2 FIX: Wrapper should NOT scale - only canvas scales via transform
            // The wrapper provides a fixed-size container for the scaled canvas
            width: `${canvasDimensions.width + 16}px`,
            minWidth: `${canvasDimensions.width + 16}px`,
            minHeight: `${dynamicCanvasHeight + 16}px`,
            padding: '8px', // Small padding to show background frame
          }}
        >
          {/* Responsive canvas container */}
          <div
            ref={(el) => {
              // Set the main canvas ref
              if (el) {
                canvasRef.current = el;
                // Set canvas element state to trigger marquee selection hook
                setCanvasElement(el);
              }
            }}
            data-responsive-canvas
            data-testid="canvas"
            role="main"
            aria-label={`Responsive canvas for ${viewport.name} viewport, ${canvasDimensions.width} by ${dynamicCanvasHeight} pixels`}
            tabIndex={0}
            className="relative cursor-default focus:outline-none focus:ring-2 focus:ring-primary/50"
            style={{
              width: `${canvasDimensions.width}px`,
              maxWidth: canvasDimensions.cssWidth,
              // FIX: Use dynamically calculated height that grows/shrinks with content
              // This allows canvas to expand when components grow and shrink when they're removed
              height: `${dynamicCanvasHeight}px`,
              minHeight: `${canvasDimensions.height}px`,
              // Week 2: Apply combined effective scale (viewportScale × userZoom)
              transform: `scale(${effectiveScale})`,
              transformOrigin: "top left",
              // Week 2 Day 6.4: Smooth 300ms transition for viewport changes
              transition: "transform 0.3s ease-out",
              borderRadius: effectiveScale < 0.8 ? "8px" : "4px",
              boxShadow:
                effectiveScale < 0.8
                  ? "0 4px 12px rgba(0,0,0,0.08)"
                  : "0 2px 4px rgba(0,0,0,0.04)",
              ...canvasBackgroundStyle,
              overflow: canvasSettings.layout.overflow,
            }}
            onClick={handleCanvasClick}
          >
            {/* Empty state */}
            {visibleComponents.length === 0 && (
              <div
                className="absolute inset-0 flex items-center justify-center pointer-events-none"
                style={{ zIndex: 2 }}
              >
                <div className="text-center space-y-4 p-8">
                  <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-primary/10 to-primary/30 flex items-center justify-center">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-primary/80 animate-pulse" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-lg font-medium text-foreground/80">
                      Responsive {viewport.name} View
                    </h3>
                    <p className="text-sm text-muted-foreground max-w-md">
                      Components will adapt responsively across all viewports
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Render components with responsive wrapper */}
            <div className="relative" style={{ zIndex: 10 }}>
              {visibleComponents
                .filter((component) => !component.parentId)
                .map((component) => (
                  <ComponentWrapper
                    key={component.id}
                    component={component}
                    viewport={viewport}
                    activeColorScheme={renderColorScheme}
                    containerDimensions={responsiveContainerDimensions}
                    canvasWidth={canvasDimensions.width}
                    canvasHeight={canvasDimensions.height}
                    enableVirtualization={performanceHints.enableVirtualization}
                    viewportBounds={performanceHints.viewportBounds}
                    spatialIndex={spatialIndex}
                    onComponentInteraction={(type, data) => {
                      if (type === "resize") {
                        const size = getInteractionSize(data);
                        if (size) {
                          handleSizeChange(component.id, size);
                        }
                      } else if (type === "drag-end") {
                        const position = getInteractionPosition(data);
                        if (position) {
                          handlePositionChange(component.id, position);
                        }
                      } else if (type === "select") {
                        onComponentSelect(component.id, getInteractionMultiSelect(data));
                      }
                    }}
                  />
                ))}
            </div>

            {/* Sketch Overlay for Plan Mode */}
            <SketchOverlay
              width={canvasDimensions.width}
              height={canvasDimensions.height}
              className="sketch-overlay"
            />

            {/* Week 3: Marquee Selection Overlay */}
            {isMarqueeActive && marqueeRectangle && (
              <MarqueeOverlay rectangle={marqueeRectangle} />
            )}
          </div>
        </div>
      </div>

      {/* Transition progress indicator */}
      {isTransitioning && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-blue-500/90 text-white text-xs px-4 py-2 rounded-full shadow-lg">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
            <span>
              Transitioning to {viewport.name} ({Math.round(transitionProgress * 100)}%)
            </span>
          </div>
        </div>
      )}

      {/* Week 3: Selection Counter (only show in active viewport) */}
      {isActive && (
        <SelectionCounter
          position="bottom-left"
          showActions={true}
          onAction={(action) => {
            logger.debug("Selection counter action triggered", { action });
          }}
        />
      )}
        </Card>
      </TableModalPreviewProvider>
    </InteractionContextProvider>
  );
};
